/** The Run Kinematics report tab */
import { KinematicsGraphV2 } from '@/pages/ReportV2/components/Graphs';
import { IonCardContent, IonCardTitle, IonCol, IonFabButton, IonGrid, IonIcon, IonRow, IonText } from '@ionic/react';
import { colorPalette } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';

import { KinematicsCardHeader } from '@/components/CardHeader/KinematicsCardHeader';
import { Card } from '@/components/Cards';
import { Icon } from '@/components/Icon';
import { InfoToast } from '@/components/Toasts/InfoToast';
import { LegendToast } from '@/components/Toasts/LegendToast';

import ToggleLeft from '@/assets/icons/toggle-left.svg';

import { RunKinematicData } from '@/models/reportV2.model';

import styles from '@/pages/ReportV2/Styles/Reports.module.css';

type KinematicsReportProps = {
  tabData: RunKinematicData;
  setFloatingButtons: (buttons: React.ReactNode) => void;
};

/** Run kinematics report tab
 * @param report - the full run report info and data
 */
export const RunKinematicsReportTab = ({ tabData, setFloatingButtons }: KinematicsReportProps) => {
  const [showAllStrides, setShowAllStrides] = useState<boolean>(false);
  const [showToast, setShowToast] = useState(false);
  const [showInfoToast, setShowInfoToast] = useState(false);

  const handleShowToast = () => {
    setShowToast(true);
  };

  const handleCloseToast = () => {
    setShowToast(false);
  };

  const toggleShowAllStrides = () => {
    setShowAllStrides((prev) => {
      const newValue = !prev;
      localStorage.setItem('showAllStridesKinematics', newValue ? 'true' : 'false');
      return newValue;
    });
  };

  const seriesPelvis = useMemo(() => {
    return showAllStrides ? tabData.pelvisKinematicData.allStrides : tabData.pelvisKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesHip = useMemo(() => {
    return showAllStrides ? tabData.hipKinematicData.allStrides : tabData.hipKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesKnee = useMemo(() => {
    return showAllStrides ? tabData.kneeKinematicData.allStrides : tabData.kneeKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesAnkle = useMemo(() => {
    return showAllStrides ? tabData.ankleKinematicData.allStrides : tabData.ankleKinematicData.mean;
  }, [showAllStrides, tabData]);

  useEffect(() => {
    setShowInfoToast(true);
    return () => {
      setShowInfoToast(false);
    };
  }, []);

  useEffect(() => {
    const buttons = (
      <>
        <IonFabButton onClick={() => toggleShowAllStrides()} title={'Toggle Mean / All Strides'}>
          <IonIcon icon={ToggleLeft}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={handleShowToast} title={'Show Graph Legend'}>
          <IonIcon icon={colorPalette}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={() => setShowInfoToast(true)} title={'Show Info'}>
          <Icon name={'info'}></Icon>
        </IonFabButton>
      </>
    );
    setFloatingButtons(buttons); // Update buttons in parent
    return () => setFloatingButtons(null); // Clean up on unmount
  }, [setFloatingButtons]);

  if (!tabData) {
    return <IonText>Data not found.</IonText>;
  }
  return (
    <>
      {/* <IonContent className={mergeClassNames('ion-padding', styles.content)} id='main' color='light'> */}
      <IonRow className='ion-no-padding'>
        <IonCol className='' size='12'>
          <Card style={{ height: 'fit-content', marginLeft: 0, marginRight: 0, marginTop: 0 }}>
            <KinematicsCardHeader />
            <IonCardContent>
              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px', backgroundColor: 'transparent' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Pelvis</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesPelvis.Sagittal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Anterior tilt (-) / Posterior tilt (+)'}
                        title={'Pelvis - Sagittal'}
                        yAxis={seriesPelvis.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesPelvis.Coronal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Drop (-) / Hike (+)'}
                        title={'Pelvis - Frontal'}
                        yAxis={seriesPelvis.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesPelvis.Transversal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Backward Rotation (-) / Forward Rotation (+)'}
                        title={'Pelvis - Transversal'}
                        yAxis={seriesPelvis.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Hip</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesHip.Sagittal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Extension (-) / Flexion (+)'}
                        title={'Hip - Sagittal'}
                        yAxis={seriesHip.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesHip.Coronal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Abduction (-) / Adduction (+)'}
                        title={'Hip - Frontal'}
                        yAxis={seriesHip.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesHip.Transversal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Hip - Transversal'}
                        yAxis={seriesHip.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Knee</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesKnee.Sagittal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Extension (-) / Flexion (+)'}
                        title={'Knee - Sagittal'}
                        yAxis={seriesKnee.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesKnee.Coronal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Valgus (-) / Varus (+)'}
                        title={'Knee - Frontal'}
                        yAxis={seriesKnee.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesKnee.Transversal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Knee - Transversal'}
                        yAxis={seriesKnee.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              <IonRow>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Ankle</IonCardTitle>
                  </IonRow>
                  <IonRow>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesAnkle.Sagittal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Plantarflexion (-) / Dorsalflexion (+)'}
                        title={'Ankle - Sagittal'}
                        yAxis={seriesAnkle.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesAnkle.Coronal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Eversion (-) / Inversion (+)'}
                        title={'Ankle - Frontal'}
                        yAxis={seriesAnkle.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesAnkle.Transversal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Ankle - Transversal'}
                        yAxis={seriesAnkle.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>
            </IonCardContent>
          </Card>
        </IonCol>
      </IonRow>

      <LegendToast
        duration={3000} // 5 seconds
        isOpen={showToast}
        onClose={handleCloseToast}
      />
      <InfoToast
        isOpen={showInfoToast}
        message='All graphs can be enlarged by clicking / tapping on the graph.'
        onClose={() => setShowInfoToast(false)}
      />
      {/* </IonContent> */}
    </>
  );
};
